import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { type ExportFormat, useReportBuilderStore } from '../../stores/reportBuilderStore'
import {
	type DistributionReportResult,
	type DistributionRow,
	getOperatorLabel,
	type PivotReportResult,
	type PivotRow,
	type RawDataColumn,
	type RawDataRow,
	type ReportBuilderConfig,
	type ReportRow,
	resolveFieldLabel,
	type VisibleColumn,
} from '../../types'
import { PivotBarChart, PivotLineChart } from '../charts'

// V3.2: Pivot visualization modes
type PivotViewMode = 'table' | 'bar' | 'line'

function formatFilterDisplay(f: { field: string; operator: string; values: string[] }): string {
	return `${resolveFieldLabel(f.field)} ${getOperatorLabel(f.operator)} ${f.values.join(', ')}`
}

function ReportParametersFields({
	config,
	isPivotMode,
	isDistributionMode,
}: {
	config: {
		groupBy: string[]
		groupBySource?: string
		aggregateBy: string[]
		columnFields?: { field: string }[]
		distributionComponentId?: string
		filters: { field: string; operator: string; values: string[] }[]
		filterLogic?: string
	}
	isPivotMode?: boolean
	isDistributionMode?: boolean
}) {
	return (
		<>
			{config.groupBy.length > 0 && (
				<>
					<dt className='font-medium text-neutral-500'>Group By</dt>
					<dd className='text-neutral-900'>
						{config.groupBy.map((g) => resolveFieldLabel(g)).join(', ')}
						{config.groupBySource && (
							<span className='text-neutral-400 ml-1'>({config.groupBySource.replace('_', ' ')})</span>
						)}
					</dd>
				</>
			)}
			{config.aggregateBy.length > 0 && (
				<>
					<dt className='font-medium text-neutral-500'>Aggregation</dt>
					<dd className='text-neutral-900'>{config.aggregateBy.join(', ')}</dd>
				</>
			)}
			{isPivotMode && config.columnFields && config.columnFields.length > 0 && (
				<>
					<dt className='font-medium text-neutral-500'>Pivot Columns</dt>
					<dd className='text-neutral-900'>
						{config.columnFields.map((cf) => resolveFieldLabel(cf.field)).join(', ')}
					</dd>
				</>
			)}
			{isDistributionMode && config.distributionComponentId && (
				<>
					<dt className='font-medium text-neutral-500'>Distribution Component</dt>
					<dd className='text-neutral-900'>{config.distributionComponentId}</dd>
				</>
			)}
			{config.filters.length > 0 && (
				<>
					<dt className='font-medium text-neutral-500'>Filters</dt>
					<dd className='text-neutral-900'>
						{config.filters.map((f, i) => (
							<span key={`${f.field}-${f.operator}-${i}`}>
								{i > 0 && <span className='text-xs font-medium text-neutral-400 mx-1'>{config.filterLogic}</span>}
								{formatFilterDisplay(f)}
							</span>
						))}
					</dd>
				</>
			)}
		</>
	)
}

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']

/**
 * V2.1: Centralized report data table component
 * Uses columns from API metadata (SINGLE SOURCE OF TRUTH)
 */
function ReportDataTable({
	data,
	columns,
	showRowNumber,
}: {
	data: ReportRow[]
	columns: VisibleColumn[]
	showRowNumber?: boolean
}) {
	// Filter columns: skip rowType in preview (internal use only)
	const visibleColumns = columns.filter((col) => col.key !== 'rowType')

	// Render cell value based on column format
	const renderCell = (row: ReportRow, col: VisibleColumn): React.ReactNode => {
		// Handle rating distribution columns (key format: ratingDistribution.N)
		if (col.key.startsWith('ratingDistribution.')) {
			const ratingNum = Number.parseInt(col.key.split('.')[1], 10)
			const value = row.ratingDistribution?.[ratingNum]
			return value ?? 0
		}

		const value = row[col.key as keyof ReportRow]

		if (value === null || value === undefined) return '-'

		// Only handle primitive types, skip complex objects
		if (typeof value === 'object') return '-'

		switch (col.format) {
			case 'number':
				return typeof value === 'number' ? value.toFixed(2) : String(value)
			case 'date':
				return typeof value === 'string' ? new Date(value).toLocaleDateString() : '-'
			case 'badge':
				return <span className='px-1.5 py-0.5 bg-neutral-100 rounded text-[10px] uppercase'>{String(value)}</span>
			default:
				return String(value)
		}
	}

	return (
		<table className='min-w-full divide-y divide-neutral-200'>
			<caption className='sr-only'>Report data preview showing {data.length} rows</caption>
			<thead className='bg-neutral-50 sticky top-0 z-10'>
				<tr>
					{showRowNumber && (
						<th
							scope='col'
							className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
							#
						</th>
					)}
					{visibleColumns.map((col) => (
						<th
							key={col.key}
							scope='col'
							className={`px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider ${
								col.align === 'right' ? 'text-right' : 'text-left'
							}`}>
							{col.header}
						</th>
					))}
				</tr>
			</thead>
			<tbody className='bg-white divide-y divide-neutral-200'>
				{data.map((row, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: generic report data rows have no stable identifier
					<tr key={index} className='hover:bg-neutral-50'>
						{showRowNumber && <td className='px-4 py-2 text-sm text-neutral-400 text-right'>{index + 1}</td>}
						{visibleColumns.map((col) => (
							<td
								key={col.key}
								className={`px-4 py-2 text-sm ${
									col.align === 'right' ? 'text-right' : 'text-left'
								} ${col.key === 'formName' ? 'text-neutral-900' : 'text-neutral-600'} ${
									col.key === 'response' ? 'max-w-[200px] truncate' : ''
								}`}
								title={col.key === 'response' ? String(row.response || '') : undefined}>
								{renderCell(row, col)}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	)
}

/**
 * V3.3: Distribution Data Table - renders distribution analysis
 */
function DistributionDataTable({
	result,
	paginatedRows,
	showRowNumber,
}: {
	result: DistributionReportResult
	paginatedRows?: DistributionRow[]
	showRowNumber?: boolean
}) {
	const rows = paginatedRows ?? result.distribution

	return (
		<table className='min-w-full divide-y divide-neutral-200'>
			<caption className='sr-only'>Distribution analysis showing {rows.length} response values</caption>
			<thead className='bg-neutral-50 sticky top-0 z-10'>
				<tr>
					{showRowNumber && (
						<th
							scope='col'
							className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
							#
						</th>
					)}
					<th scope='col' className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-left'>
						Response Value
					</th>
					<th
						scope='col'
						className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
						Count
					</th>
					<th
						scope='col'
						className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
						Percentage
					</th>
				</tr>
			</thead>
			<tbody className='bg-white divide-y divide-neutral-200'>
				{rows.map((row, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: generic report data rows have no stable identifier
					<tr key={index} className='hover:bg-neutral-50'>
						{showRowNumber && <td className='px-4 py-2 text-sm text-neutral-400 text-right'>{index + 1}</td>}
						<td className='px-4 py-2 text-sm text-neutral-900 text-left'>{row.responseValue || '-'}</td>
						<td className='px-4 py-2 text-sm text-neutral-600 text-right'>{row.count}</td>
						<td className='px-4 py-2 text-sm text-neutral-600 text-right'>{row.percentage.toFixed(1)}%</td>
					</tr>
				))}
			</tbody>
		</table>
	)
}

/**
 * V2.5: Pivot Data Table - renders cross-tabulated data
 * V3.3.1: Updated to support multi-aggregation rendering with composite keys
 */
function PivotDataTable({
	result,
	paginatedRows,
	visibleColumnValues,
	showRowNumber,
}: {
	result: PivotReportResult
	paginatedRows?: PivotRow[]
	visibleColumnValues?: string[]
	showRowNumber?: boolean
}) {
	const { rowAxis, valueConfigs } = result
	const columnValues = visibleColumnValues ?? result.columnValues
	const rows = paginatedRows ?? result.rows

	const renderCell = (value: number | null): React.ReactNode => {
		if (value === null || value === undefined) return '-'
		return value.toFixed(2)
	}

	// V3.3.1: Check if we have multiple aggregations
	const hasMultipleAggregations = valueConfigs && valueConfigs.length > 1

	// V3.3.1: Generate column headers with aggregation suffix for multi-agg mode
	const columnHeaders = useMemo(() => {
		if (!hasMultipleAggregations) {
			return columnValues.map((colVal) => ({ key: colVal, label: colVal }))
		}

		const headers: { key: string; label: string }[] = []
		for (const colVal of columnValues) {
			for (const vc of valueConfigs) {
				const compositeKey = `${colVal}__${vc.aggregation}`
				const label = `${colVal} (${vc.aggregation})`
				headers.push({ key: compositeKey, label })
			}
		}
		return headers
	}, [columnValues, valueConfigs, hasMultipleAggregations])

	return (
		<table className='min-w-full divide-y divide-neutral-200'>
			<caption className='sr-only'>Pivot table showing {rows.length} rows</caption>
			<thead className='bg-neutral-50 sticky top-0 z-10'>
				<tr>
					{showRowNumber && (
						<th
							scope='col'
							className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
							#
						</th>
					)}
					{rowAxis.map((rf) => (
						<th
							key={rf.field}
							scope='col'
							className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-left'>
							{rf.field}
						</th>
					))}
					{columnHeaders.map((col) => (
						<th
							key={col.key}
							scope='col'
							className='px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wider text-right'>
							{col.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody className='bg-white divide-y divide-neutral-200'>
				{rows.map((row, index) => (
					<tr
						// biome-ignore lint/suspicious/noArrayIndexKey: generic report data rows have no stable identifier
						key={index}
						className={`hover:bg-neutral-50 ${
							row.rowType === 'subtotal' ? 'bg-neutral-100 font-medium' : ''
						} ${row.rowType === 'grand-total' ? 'bg-neutral-200 font-bold' : ''}`}>
						{showRowNumber && <td className='px-4 py-2 text-sm text-neutral-400 text-right'>{index + 1}</td>}
						{rowAxis.map((rf) => (
							<td key={rf.field} className='px-4 py-2 text-sm text-neutral-900 text-left'>
								{row.rowValues[rf.field] ?? '-'}
							</td>
						))}
						{columnHeaders.map((col) => (
							<td key={col.key} className='px-4 py-2 text-sm text-neutral-600 text-right'>
								{renderCell(row.cells[col.key])}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	)
}

function RawDataTable({
	data,
	columns,
	hiddenColumns,
	showRowNumber,
	stickyHeader,
	hoverable,
	caption,
}: {
	data: RawDataRow[]
	columns: RawDataColumn[]
	hiddenColumns: Set<string>
	showRowNumber?: boolean
	stickyHeader?: boolean
	hoverable?: boolean
	caption: string
}) {
	return (
		<table className='min-w-full divide-y divide-neutral-200'>
			<caption className='sr-only'>{caption}</caption>
			<thead className={`bg-neutral-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
				<tr>
					{showRowNumber && (
						<th
							scope='col'
							className='px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							#
						</th>
					)}
					{!hiddenColumns.has('_participant') && (
						<th
							scope='col'
							className='px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							Participant
						</th>
					)}
					{!hiddenColumns.has('_form') && (
						<th
							scope='col'
							className='px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							Form
						</th>
					)}
					{!hiddenColumns.has('_repeatable') && (
						<th
							scope='col'
							className='px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							Repeatable
						</th>
					)}
					{!hiddenColumns.has('_instance') && (
						<th
							scope='col'
							className='px-4 py-2 text-center text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							Instance #
						</th>
					)}
					{columns.map((col) => (
						<th
							key={col.id}
							scope='col'
							className='px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider'>
							{col.label}
						</th>
					))}
				</tr>
			</thead>
			<tbody className='bg-white divide-y divide-neutral-200'>
				{data.map((row, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: generic report data rows have no stable identifier
					<tr key={index} className={hoverable ? 'hover:bg-neutral-50' : ''}>
						{showRowNumber && <td className='px-4 py-2 text-sm text-neutral-400 text-right'>{index + 1}</td>}
						{!hiddenColumns.has('_participant') && (
							<td className='px-4 py-2 text-sm text-neutral-900'>
								{row.participantName || row.participantEmail || '-'}
							</td>
						)}
						{!hiddenColumns.has('_form') && <td className='px-4 py-2 text-sm text-neutral-600'>{row.formName}</td>}
						{!hiddenColumns.has('_repeatable') && (
							<td className='px-4 py-2 text-sm text-neutral-600'>{row.repeatableLabel}</td>
						)}
						{!hiddenColumns.has('_instance') && (
							<td className='px-4 py-2 text-sm text-neutral-600 text-center'>{row.instanceIndex + 1}</td>
						)}
						{columns.map((col) => (
							<td key={col.id} className='px-4 py-2 text-sm text-neutral-600'>
								{row.fields[col.label] ?? '-'}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	)
}

function ReportParametersContent({
	currentConfig,
	forms,
	cycles,
	isPivotMode,
	isDistributionMode,
	isRawDataMode,
	gapY = 'gap-y-2',
}: {
	currentConfig: ReportBuilderConfig
	forms: { id: string; name: string }[]
	cycles: { id: string; name: string }[]
	isPivotMode: boolean
	isDistributionMode: boolean
	isRawDataMode: boolean
	gapY?: string
}) {
	return (
		<dl className={`grid grid-cols-[auto_1fr] gap-x-4 ${gapY} text-sm`}>
			<dt className='font-medium text-neutral-500'>Report Type</dt>
			<dd className='text-neutral-900'>
				{isDistributionMode ? 'Distribution' : isRawDataMode ? 'Raw Data' : isPivotMode ? 'Pivot' : 'Standard'}
			</dd>

			<dt className='font-medium text-neutral-500'>Forms</dt>
			<dd className='text-neutral-900'>
				{currentConfig.selectedFormIds.map((id) => forms.find((f) => f.id === id)?.name || id).join(', ') || '-'}
			</dd>

			<dt className='font-medium text-neutral-500'>Cycles</dt>
			<dd className='text-neutral-900'>
				{currentConfig.selectedCycleIds.map((id) => cycles.find((c) => c.id === id)?.name || id).join(', ') || '-'}
			</dd>

			<ReportParametersFields
				config={currentConfig}
				isPivotMode={isPivotMode}
				isDistributionMode={isDistributionMode}
			/>
		</dl>
	)
}

export function StepPreview() {
	const {
		reportResult,
		pivotResult,
		rawDataResult,
		distributionResult,
		currentConfig,
		exportReport,
		exportRawDataReport,
		exportPivotReport,
		exportDistributionReport,
		saveConfig,
		prevStep,
		isExporting,
		isSaving,
		forms,
		cycles,
	} = useReportBuilderStore()

	const [pivotViewMode, setPivotViewMode] = useState<PivotViewMode>('table')
	const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())
	const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
	const [showVisibilityPanel, setShowVisibilityPanel] = useState(false)
	const [showParamsPanel, setShowParamsPanel] = useState(false)

	const isRawDataMode = currentConfig.reportType === 'raw-data'
	const isDistributionMode = currentConfig.reportType === 'distribution'
	const isPivotMode = currentConfig.columnFields && currentConfig.columnFields.length > 0
	const activeResult = isDistributionMode
		? distributionResult
		: isRawDataMode
			? rawDataResult
			: isPivotMode
				? pivotResult
				: reportResult

	const MAX_CHART_GROUPS = 20

	const { chartData, totalGroups } = useMemo(() => {
		if (isRawDataMode || !reportResult?.data) return { chartData: [], totalGroups: 0 }

		const grouped = new Map<string, { sum: number; count: number }>()
		for (const row of reportResult.data) {
			const key = row.groupKey || 'Unknown'
			const existing = grouped.get(key) || { sum: 0, count: 0 }
			if (row.averageRating !== undefined && row.averageRating !== null) {
				existing.sum += row.averageRating
				existing.count += 1
			} else if (row.count !== undefined && row.count !== null) {
				existing.count += row.count
			}
			grouped.set(key, existing)
		}

		const all = Array.from(grouped.entries())
			.map(([name, data]) => ({
				name,
				average: data.count > 0 ? Number((data.sum / data.count).toFixed(2)) : 0,
				count: data.count,
			}))
			.sort((a, b) => b.average - a.average)

		return { chartData: all.slice(0, MAX_CHART_GROUPS), totalGroups: all.length }
	}, [reportResult, isRawDataMode])

	const distributionData = useMemo(() => {
		if (isRawDataMode || !reportResult?.data) return []

		const distribution: Record<number, number> = {}
		for (const row of reportResult.data) {
			if (row.ratingDistribution) {
				for (const [rating, count] of Object.entries(row.ratingDistribution)) {
					const ratingNum = Number.parseInt(rating, 10)
					distribution[ratingNum] = (distribution[ratingNum] || 0) + count
				}
			}
		}

		return Object.entries(distribution)
			.map(([rating, count]) => ({
				name: `Rating ${rating}`,
				value: count,
				rating: Number(rating),
			}))
			.sort((a, b) => a.rating - b.rating)
	}, [reportResult, isRawDataMode])

	const handleExport = (format: ExportFormat) => {
		if (isDistributionMode) {
			exportDistributionReport(format)
		} else if (isRawDataMode) {
			exportRawDataReport(format)
		} else if (isPivotMode && pivotResult) {
			exportPivotReport(format)
		} else {
			exportReport(format)
		}
	}

	const ROWS_PER_PAGE = 10
	const [currentPage, setCurrentPage] = useState(1)

	useEffect(() => {
		setCurrentPage(1)
	}, [])

	const totalRows = useMemo(() => {
		if (isDistributionMode) {
			return distributionResult?.distribution?.length || 0
		}
		if (isRawDataMode) {
			return rawDataResult?.data?.length || 0
		}
		if (isPivotMode) {
			return pivotResult?.rows?.length || 0
		}
		return reportResult?.data?.length || 0
	}, [isDistributionMode, isRawDataMode, isPivotMode, distributionResult, rawDataResult, pivotResult, reportResult])

	const totalPages = useMemo(() => {
		return Math.ceil(totalRows / ROWS_PER_PAGE)
	}, [totalRows])

	const paginatedData = useMemo(() => {
		const start = (currentPage - 1) * ROWS_PER_PAGE
		const end = start + ROWS_PER_PAGE

		if (isDistributionMode && distributionResult?.distribution) {
			return distributionResult.distribution.slice(start, end)
		}
		if (isRawDataMode && rawDataResult?.data) {
			return rawDataResult.data.slice(start, end)
		}
		if (isPivotMode && pivotResult?.rows) {
			return pivotResult.rows.slice(start, end)
		}
		if (!isDistributionMode && !isRawDataMode && !isPivotMode && reportResult?.data) {
			return reportResult.data.slice(start, end)
		}
		return []
	}, [
		isDistributionMode,
		isRawDataMode,
		isPivotMode,
		distributionResult,
		rawDataResult,
		pivotResult,
		reportResult,
		currentPage,
	])

	const allData = useMemo(() => {
		if (isDistributionMode && distributionResult?.distribution) {
			return distributionResult.distribution
		}
		if (isRawDataMode && rawDataResult?.data) {
			return rawDataResult.data
		}
		if (isPivotMode && pivotResult?.rows) {
			return pivotResult.rows
		}
		if (!isDistributionMode && !isRawDataMode && !isPivotMode && reportResult?.data) {
			return reportResult.data
		}
		return []
	}, [isDistributionMode, isRawDataMode, isPivotMode, distributionResult, rawDataResult, pivotResult, reportResult])

	const rawDataColumns = useMemo(() => {
		if (!isRawDataMode || !rawDataResult?.metadata?.columns) return []
		return rawDataResult.metadata.columns
	}, [isRawDataMode, rawDataResult])

	useEffect(() => {
		setHiddenColumns(new Set())
		setHiddenSeries(new Set())
	}, [])

	const toggleColumn = (key: string) => {
		setHiddenColumns((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}

	const toggleSeries = (key: string) => {
		setHiddenSeries((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}

	const availableColumns = useMemo((): { key: string; label: string }[] => {
		if (isRawDataMode && rawDataResult) {
			return [
				{ key: '_participant', label: 'Participant' },
				{ key: '_form', label: 'Form' },
				{ key: '_repeatable', label: 'Repeatable' },
				{ key: '_instance', label: 'Instance #' },
				...rawDataColumns.map((c) => ({ key: c.id, label: c.label })),
			]
		}
		if (isPivotMode && pivotResult) {
			return pivotResult.columnValues.map((v) => ({ key: v, label: v }))
		}
		if (isDistributionMode && distributionResult) {
			return distributionResult.distribution.map((d) => ({
				key: d.responseValue,
				label: d.responseValue || 'N/A',
			}))
		}
		if (reportResult?.metadata?.columns) {
			return reportResult.metadata.columns
				.filter((c) => c.key !== 'rowType')
				.map((c) => ({ key: c.key, label: c.header }))
		}
		return []
	}, [
		isRawDataMode,
		isPivotMode,
		isDistributionMode,
		rawDataResult,
		pivotResult,
		distributionResult,
		reportResult,
		rawDataColumns,
	])

	const visibilityTarget = isPivotMode || isDistributionMode ? 'series' : 'columns'

	const filteredReportColumns = useMemo(() => {
		if (!reportResult?.metadata?.columns) return []
		return reportResult.metadata.columns.filter((c) => c.key === 'rowType' || !hiddenColumns.has(c.key))
	}, [reportResult, hiddenColumns])

	const filteredRawDataColumns = useMemo(() => {
		return rawDataColumns.filter((c) => !hiddenColumns.has(c.id))
	}, [rawDataColumns, hiddenColumns])

	const filteredPivotColumnValues = useMemo(() => {
		if (!pivotResult) return []
		return pivotResult.columnValues.filter((v) => !hiddenSeries.has(v))
	}, [pivotResult, hiddenSeries])

	const filteredDistributionRows = useMemo(() => {
		if (!distributionResult) return []
		return distributionResult.distribution.filter((d) => !hiddenSeries.has(d.responseValue))
	}, [distributionResult, hiddenSeries])

	const goToPage = (page: number) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page)
		}
	}

	if (!activeResult) {
		return (
			<div className='text-center py-12'>
				<p className='text-neutral-500'>No report data available. Please generate a report first.</p>
				<button
					type='button'
					onClick={prevStep}
					className='mt-4 px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50'>
					Go Back
				</button>
			</div>
		)
	}

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-lg font-semibold text-neutral-900'>
						<span className='no-print'>
							{currentConfig.name && <span className='text-neutral-700'>"{currentConfig.name}" — </span>}
							{isDistributionMode
								? 'Distribution Report Preview'
								: isRawDataMode
									? 'Raw Data Preview'
									: 'Report Preview'}
						</span>
						<span className='print-only'>{currentConfig.name || 'Report'}</span>
					</h2>
					<p className='text-sm text-neutral-600'>
						Generated at {new Date(activeResult.metadata.generatedAt).toLocaleString()}
					</p>
				</div>
				<div className='flex items-center gap-3 no-print'>
					<button
						type='button'
						onClick={() => saveConfig()}
						disabled={isSaving}
						className='flex items-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 text-sm font-medium disabled:opacity-50'>
						{isSaving ? (
							<svg className='animate-spin h-4 w-4' viewBox='0 0 24 24' aria-hidden='true'>
								<circle
									className='opacity-25'
									cx='12'
									cy='12'
									r='10'
									stroke='currentColor'
									strokeWidth='4'
									fill='none'
								/>
								<path
									className='opacity-75'
									fill='currentColor'
									d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
								/>
							</svg>
						) : (
							<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4'
								/>
							</svg>
						)}
						{isSaving ? 'Saving...' : 'Save Configuration'}
					</button>
					<div className='flex items-center gap-2'>
						<button
							type='button'
							onClick={() => handleExport('xlsx')}
							disabled={isExporting}
							className='px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50'>
							{isExporting ? 'Exporting...' : 'Export Excel'}
						</button>
						<button
							type='button'
							onClick={() => handleExport('csv')}
							disabled={isExporting}
							className='px-4 py-2 bg-neutral-600 text-white rounded-md hover:bg-neutral-700 text-sm font-medium disabled:opacity-50'>
							{isExporting ? 'Exporting...' : 'Export CSV'}
						</button>
						<button
							type='button'
							onClick={() => {
								let colCount = 0
								if (isDistributionMode) {
									colCount = 3
								} else if (isRawDataMode) {
									colCount = filteredRawDataColumns.length + 4
								} else if (isPivotMode && pivotResult) {
									colCount = filteredPivotColumnValues.length + (pivotResult.rowAxis?.length || 0)
								} else {
									colCount = filteredReportColumns.length
								}
								let landscapeStyle: HTMLStyleElement | null = null
								if (colCount > 6) {
									document.documentElement.classList.add('print-landscape')
									landscapeStyle = document.createElement('style')
									landscapeStyle.textContent = '@page { size: A4 landscape !important; margin: 0.5cm !important; }'
									document.head.appendChild(landscapeStyle)
								}
								window.print()
								document.documentElement.classList.remove('print-landscape')
								if (landscapeStyle) landscapeStyle.remove()
							}}
							className='px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium'>
							Export PDF
						</button>
					</div>
				</div>
			</div>

			<div
				className={`grid grid-cols-1 ${isRawDataMode ? 'md:grid-cols-4' : isDistributionMode ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 no-print`}>
				<div className='p-4 bg-blue-50 border border-blue-200 rounded-lg text-center'>
					<p className='text-sm text-blue-600 font-medium'>{isDistributionMode ? 'Total Responses' : 'Total Rows'}</p>
					<p className='text-2xl font-bold text-blue-900'>
						{isDistributionMode && distributionResult
							? distributionResult.metadata.totalResponses
							: 'totalRows' in activeResult.metadata
								? activeResult.metadata.totalRows
								: 0}
					</p>
					{'truncated' in activeResult.metadata && activeResult.metadata.truncated && (
						<p className='text-xs text-blue-600 mt-1'>Results truncated to 10,000 rows</p>
					)}
				</div>
				<div className='p-4 bg-green-50 border border-green-200 rounded-lg text-center'>
					<p className='text-sm text-green-600 font-medium'>Forms Included</p>
					<p className='text-2xl font-bold text-green-900'>{activeResult.metadata.forms.length}</p>
				</div>
				<div className='p-4 bg-purple-50 border border-purple-200 rounded-lg text-center'>
					<p className='text-sm text-purple-600 font-medium'>Cycles Included</p>
					<p className='text-2xl font-bold text-purple-900'>{activeResult.metadata.cycles.length}</p>
				</div>
				{isDistributionMode && distributionResult && (
					<div className='p-4 bg-orange-50 border border-orange-200 rounded-lg text-center'>
						<p className='text-sm text-orange-600 font-medium'>Unique Values</p>
						<p className='text-2xl font-bold text-orange-900'>{distributionResult.metadata.uniqueValues}</p>
					</div>
				)}
				{isRawDataMode && rawDataResult && (
					<div className='p-4 bg-orange-50 border border-orange-200 rounded-lg text-center'>
						<p className='text-sm text-orange-600 font-medium'>Repeatables</p>
						<p className='text-2xl font-bold text-orange-900'>{rawDataResult.metadata.repeatables.length}</p>
					</div>
				)}
			</div>

			{/* V3.3: Distribution mode charts */}
			{isDistributionMode && distributionResult && filteredDistributionRows.length > 0 && (
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
					<div className='border border-neutral-200 rounded-lg p-4'>
						<h3 className='text-sm font-medium text-neutral-700 mb-4'>
							Distribution: {distributionResult.responseFieldLabel}
						</h3>
						<div className='h-64'>
							<ResponsiveContainer width='100%' height='100%' minWidth={0} minHeight={0}>
								<PieChart>
									<Pie
										data={filteredDistributionRows.map((d) => ({
											name: d.responseValue || 'N/A',
											value: d.count,
										}))}
										cx='50%'
										cy='50%'
										labelLine={false}
										label={({ name, percent }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
										outerRadius={80}
										fill='#8884d8'
										dataKey='value'>
										{filteredDistributionRows.map((_, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: chart cells are positional with no stable identifier
											<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
										))}
									</Pie>
									<Tooltip
										contentStyle={{
											backgroundColor: '#fff',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											fontSize: '12px',
										}}
										formatter={(value: number | undefined) => [value ?? 0, 'Count']}
									/>
								</PieChart>
							</ResponsiveContainer>
						</div>
					</div>

					<div className='border border-neutral-200 rounded-lg p-4'>
						<h3 className='text-sm font-medium text-neutral-700 mb-4'>Response Counts</h3>
						<div className='h-64'>
							<ResponsiveContainer width='100%' height='100%' minWidth={0} minHeight={0}>
								<BarChart
									data={filteredDistributionRows.map((d) => ({
										name: d.responseValue || 'N/A',
										count: d.count,
										percentage: d.percentage,
									}))}
									margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
									<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
									<XAxis dataKey='name' tick={{ fontSize: 11, fill: '#6b7280' }} />
									<YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
									<Tooltip
										contentStyle={{
											backgroundColor: '#fff',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											fontSize: '12px',
										}}
										formatter={(value: number | undefined, name?: string) => [
											value ?? 0,
											name === 'count' ? 'Count' : 'Percentage',
										]}
									/>
									<Bar dataKey='count' name='Count' fill='#3B82F6' radius={[4, 4, 0, 0]}>
										{filteredDistributionRows.map((_, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: chart cells are positional with no stable identifier
											<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
										))}
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>
			)}

			{!isRawDataMode && !isDistributionMode && chartData.length > 0 && (
				<div className='border border-neutral-200 rounded-lg p-4'>
					<h3 className='text-sm font-medium text-neutral-700 mb-4'>
						Average Ratings by {resolveFieldLabel(currentConfig.groupBy[0]) || 'Group'}
					</h3>
					<div className='h-72'>
						<ResponsiveContainer width='100%' height='100%' minWidth={0} minHeight={0}>
							<BarChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
								<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
								<XAxis
									dataKey='name'
									tick={{ fontSize: 11, fill: '#6b7280' }}
									angle={-45}
									textAnchor='end'
									height={70}
								/>
								<YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 'auto']} />
								<Tooltip
									contentStyle={{
										backgroundColor: '#fff',
										border: '1px solid #e5e7eb',
										borderRadius: '8px',
										fontSize: '12px',
									}}
									formatter={(value: number | undefined) => [(value ?? 0).toFixed(2), 'Average']}
								/>
								<Bar dataKey='average' fill='#3B82F6' radius={[4, 4, 0, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</div>
					{totalGroups > MAX_CHART_GROUPS && (
						<p className='text-xs text-neutral-500 text-center mt-2'>
							Showing top {MAX_CHART_GROUPS} of {totalGroups} groups
						</p>
					)}
				</div>
			)}

			{!isRawDataMode && !isDistributionMode && distributionData.length > 0 && (
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
					<div className='border border-neutral-200 rounded-lg p-4'>
						<h3 className='text-sm font-medium text-neutral-700 mb-4'>Rating Distribution (Chart)</h3>
						<div className='h-64'>
							<ResponsiveContainer width='100%' height='100%' minWidth={0} minHeight={0}>
								<PieChart>
									<Pie
										data={distributionData}
										cx='50%'
										cy='50%'
										labelLine={false}
										label={({ name, percent }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
										outerRadius={80}
										fill='#8884d8'
										dataKey='value'>
										{distributionData.map((_, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: chart cells are positional with no stable identifier
											<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
										))}
									</Pie>
									<Tooltip
										contentStyle={{
											backgroundColor: '#fff',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											fontSize: '12px',
										}}
										formatter={(value: number | undefined) => [value ?? 0, 'Count']}
									/>
								</PieChart>
							</ResponsiveContainer>
						</div>
					</div>

					<div className='border border-neutral-200 rounded-lg p-4'>
						<h3 className='text-sm font-medium text-neutral-700 mb-4'>Rating Distribution (Bar)</h3>
						<div className='h-64'>
							<ResponsiveContainer width='100%' height='100%' minWidth={0} minHeight={0}>
								<BarChart data={distributionData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
									<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
									<XAxis dataKey='name' tick={{ fontSize: 11, fill: '#6b7280' }} />
									<YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
									<Tooltip
										contentStyle={{
											backgroundColor: '#fff',
											border: '1px solid #e5e7eb',
											borderRadius: '8px',
											fontSize: '12px',
										}}
										formatter={(value: number | undefined) => [value ?? 0, 'Count']}
									/>
									<Bar dataKey='value' name='Count' radius={[4, 4, 0, 0]}>
										{distributionData.map((_, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: chart cells are positional with no stable identifier
											<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
										))}
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>
			)}

			{availableColumns.length > 0 && (
				<div className='border border-neutral-200 rounded-lg' data-testid='visibility-panel'>
					<button
						type='button'
						onClick={() => setShowVisibilityPanel(!showVisibilityPanel)}
						className='w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors'>
						<span className='flex items-center gap-2'>
							<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
								/>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
								/>
							</svg>
							Customize Visible {visibilityTarget === 'series' ? 'Series' : 'Columns'}
							{(hiddenColumns.size > 0 || hiddenSeries.size > 0) && (
								<span className='text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full'>
									{hiddenColumns.size + hiddenSeries.size} hidden
								</span>
							)}
						</span>
						<svg
							className={`h-4 w-4 text-neutral-400 transition-transform ${showVisibilityPanel ? 'rotate-180' : ''}`}
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							aria-hidden='true'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
						</svg>
					</button>
					{showVisibilityPanel && (
						<div className='px-4 pb-4 border-t border-neutral-200'>
							<div className='flex items-center justify-between mt-3 mb-2'>
								<p className='text-xs text-neutral-500'>
									Toggle which {visibilityTarget} appear in the report table and charts.
								</p>
								<div className='flex items-center gap-2'>
									<button
										type='button'
										onClick={() => {
											setHiddenColumns(new Set())
											setHiddenSeries(new Set())
										}}
										className='text-xs text-blue-600 hover:text-blue-800 font-medium'>
										Show All
									</button>
									<button
										type='button'
										onClick={() => {
											if (visibilityTarget === 'series') {
												setHiddenSeries(new Set(availableColumns.map((c) => c.key)))
											} else {
												setHiddenColumns(new Set(availableColumns.map((c) => c.key)))
											}
										}}
										className='text-xs text-neutral-500 hover:text-neutral-700 font-medium'>
										Hide All
									</button>
								</div>
							</div>
							<div className='flex flex-wrap gap-2'>
								{availableColumns.map((col) => {
									const isHidden =
										visibilityTarget === 'series' ? hiddenSeries.has(col.key) : hiddenColumns.has(col.key)
									return (
										<label
											key={col.key}
											className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium cursor-pointer transition-colors ${
												isHidden
													? 'bg-neutral-100 border-neutral-200 text-neutral-400 line-through'
													: 'bg-white border-blue-200 text-neutral-700 hover:bg-blue-50'
											}`}>
											<input
												type='checkbox'
												checked={!isHidden}
												onChange={() => (visibilityTarget === 'series' ? toggleSeries(col.key) : toggleColumn(col.key))}
												className='h-3 w-3 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
											/>
											{col.label}
										</label>
									)
								})}
							</div>
						</div>
					)}
				</div>
			)}

			<div className='border border-neutral-200 rounded-lg no-print' data-testid='params-panel'>
				<button
					type='button'
					onClick={() => setShowParamsPanel(!showParamsPanel)}
					className='w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors'>
					<span className='flex items-center gap-2'>
						<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
							/>
						</svg>
						Report Parameters
					</span>
					<svg
						className={`h-4 w-4 text-neutral-400 transition-transform ${showParamsPanel ? 'rotate-180' : ''}`}
						fill='none'
						viewBox='0 0 24 24'
						stroke='currentColor'
						aria-hidden='true'>
						<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
					</svg>
				</button>
				{showParamsPanel && (
					<div className='px-4 pb-4 border-t border-neutral-200 mt-3'>
						<ReportParametersContent
							currentConfig={currentConfig}
							forms={forms}
							cycles={cycles}
							isPivotMode={!!isPivotMode}
							isDistributionMode={isDistributionMode}
							isRawDataMode={isRawDataMode}
						/>
					</div>
				)}
			</div>

			<div className='border border-neutral-200 rounded-lg overflow-hidden'>
				<div className='px-4 py-3 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between no-print'>
					<h3 className='text-sm font-medium text-neutral-700'>Data Preview</h3>
					<div className='flex items-center gap-3'>
						{/* V3.2: Pivot view mode toggle */}
						{isPivotMode && pivotResult && (
							<div className='flex items-center gap-1 border border-neutral-200 rounded-md p-0.5 bg-white'>
								<button
									type='button'
									onClick={() => setPivotViewMode('table')}
									className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
										pivotViewMode === 'table' ? 'bg-blue-100 text-blue-700' : 'text-neutral-500 hover:text-neutral-700'
									}`}
									title='Table view'>
									Table
								</button>
								<button
									type='button'
									onClick={() => setPivotViewMode('bar')}
									className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
										pivotViewMode === 'bar' ? 'bg-blue-100 text-blue-700' : 'text-neutral-500 hover:text-neutral-700'
									}`}
									title='Bar chart'>
									Bar
								</button>
								<button
									type='button'
									onClick={() => setPivotViewMode('line')}
									className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
										pivotViewMode === 'line' ? 'bg-blue-100 text-blue-700' : 'text-neutral-500 hover:text-neutral-700'
									}`}
									title='Line chart'>
									Line
								</button>
							</div>
						)}
						<span className='text-xs text-neutral-500'>{totalRows} total rows</span>
					</div>
				</div>
				<div className='overflow-x-auto no-print'>
					{/* V3.2: Pivot chart views */}
					{isPivotMode && pivotResult && pivotViewMode === 'bar' ? (
						<div className='p-4'>
							<PivotBarChart pivot={{ ...pivotResult, columnValues: filteredPivotColumnValues }} height={400} />
						</div>
					) : isPivotMode && pivotResult && pivotViewMode === 'line' ? (
						<div className='p-4'>
							<PivotLineChart pivot={{ ...pivotResult, columnValues: filteredPivotColumnValues }} height={400} />
						</div>
					) : isDistributionMode && distributionResult ? (
						/* V3.3: Distribution table */
						<DistributionDataTable
							result={{
								...distributionResult,
								distribution: filteredDistributionRows,
							}}
							paginatedRows={(paginatedData as DistributionRow[]).filter((d) => !hiddenSeries.has(d.responseValue))}
						/>
					) : isRawDataMode ? (
						<RawDataTable
							data={paginatedData as RawDataRow[]}
							columns={filteredRawDataColumns}
							hiddenColumns={hiddenColumns}
							stickyHeader
							hoverable
							caption='Raw data export'
						/>
					) : isPivotMode && pivotResult ? (
						<PivotDataTable
							result={pivotResult}
							paginatedRows={paginatedData as PivotRow[]}
							visibleColumnValues={filteredPivotColumnValues}
						/>
					) : (
						<ReportDataTable data={paginatedData as ReportRow[]} columns={filteredReportColumns} />
					)}
				</div>
				{totalPages > 1 && (
					<div className='px-4 py-3 bg-neutral-50 border-t border-neutral-200 flex items-center justify-between no-print'>
						<div className='text-sm text-neutral-500'>
							Showing {(currentPage - 1) * ROWS_PER_PAGE + 1} to {Math.min(currentPage * ROWS_PER_PAGE, totalRows)} of{' '}
							{totalRows} rows
						</div>
						<div className='flex items-center gap-1'>
							<button
								type='button'
								onClick={() => goToPage(1)}
								disabled={currentPage === 1}
								className='p-1.5 rounded hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed'
								title='First page'>
								<svg
									className='h-4 w-4 text-neutral-600'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									aria-hidden='true'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M11 19l-7-7 7-7m8 14l-7-7 7-7'
									/>
								</svg>
							</button>
							<button
								type='button'
								onClick={() => goToPage(currentPage - 1)}
								disabled={currentPage === 1}
								className='p-1.5 rounded hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed'
								title='Previous page'>
								<svg
									className='h-4 w-4 text-neutral-600'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
								</svg>
							</button>
							<span className='px-3 py-1 text-sm text-neutral-700'>
								Page {currentPage} of {totalPages}
							</span>
							<button
								type='button'
								onClick={() => goToPage(currentPage + 1)}
								disabled={currentPage === totalPages}
								className='p-1.5 rounded hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed'
								title='Next page'>
								<svg
									className='h-4 w-4 text-neutral-600'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
								</svg>
							</button>
							<button
								type='button'
								onClick={() => goToPage(totalPages)}
								disabled={currentPage === totalPages}
								className='p-1.5 rounded hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed'
								title='Last page'>
								<svg
									className='h-4 w-4 text-neutral-600'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 5l7 7-7 7M5 5l7 7-7 7' />
								</svg>
							</button>
						</div>
					</div>
				)}
				{/* Print-only: render ALL rows (not paginated) for PDF export */}
				<div className='print-only'>
					{isDistributionMode && distributionResult ? (
						<DistributionDataTable
							result={{
								...distributionResult,
								distribution: filteredDistributionRows,
							}}
							showRowNumber
						/>
					) : isRawDataMode && rawDataResult ? (
						<RawDataTable
							data={allData as RawDataRow[]}
							columns={filteredRawDataColumns}
							hiddenColumns={hiddenColumns}
							showRowNumber
							caption='Raw data export - all rows'
						/>
					) : isPivotMode && pivotResult ? (
						<PivotDataTable result={pivotResult} visibleColumnValues={filteredPivotColumnValues} showRowNumber />
					) : (
						<ReportDataTable data={allData as ReportRow[]} columns={filteredReportColumns} showRowNumber />
					)}
				</div>
			</div>

			{/* Print-only: report parameters at the end of the PDF */}
			<div className='print-only border border-neutral-200 rounded-lg px-4 py-3'>
				<h3 className='text-sm font-medium text-neutral-700 mb-2'>Report Parameters</h3>
				<ReportParametersContent
					currentConfig={currentConfig}
					forms={forms}
					cycles={cycles}
					isPivotMode={!!isPivotMode}
					isDistributionMode={isDistributionMode}
					isRawDataMode={isRawDataMode}
					gapY='gap-y-1'
				/>
			</div>

			<div className='flex justify-between no-print'>
				<button
					type='button'
					onClick={prevStep}
					className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
					Back to Edit
				</button>
			</div>
		</div>
	)
}
